import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '@/components/layout/MainLayout'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'
import contractService from '@/services/contractService'
import { Garantia } from '@/types'
import { useDialog } from '@/hooks/useDialog'
import { useAuth } from '@/contexts/AuthContext'

type FormState = {
  nombre: string
  descripcion: string
}

const emptyForm: FormState = { nombre: '', descripcion: '' }

export default function AdminGarantias() {
  const navigate = useNavigate()
  const { showConfirm } = useDialog()
  const { user } = useAuth()
  const [items, setItems] = useState<Garantia[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<Garantia | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)

  const isAdmin = user?.role === 'admin'

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const res: any = await contractService.getGarantias()
      const data = res.data?.items || res.data || []
      setItems(Array.isArray(data) ? data : [])
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar garantias')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setShowModal(true)
  }

  const openEdit = (item: Garantia) => {
    if (item.es_predefinida) return
    setEditing(item)
    setForm({
      nombre: item.nombre || '',
      descripcion: item.descripcion || '',
    })
    setShowModal(true)
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
      }

      if (!payload.nombre) {
        setError('El nombre es requerido')
        return
      }

      if (editing) {
        await contractService.updateGarantia(Number(editing.id), payload)
        setMessage('Garantia actualizada')
      } else {
        await contractService.createGarantia(payload)
        setMessage('Garantia creada')
      }

      setShowModal(false)
      setEditing(null)
      setForm(emptyForm)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar la garantia')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (item: Garantia) => {
    if (item.es_predefinida) {
      setError('Las garantias predefinidas no pueden eliminarse')
      return
    }

    const confirmed = await showConfirm({
      title: 'Eliminar garantia',
      message: `¿Eliminar "${item.nombre}"?`,
      type: 'danger',
      confirmText: 'Eliminar',
    })
    if (!confirmed) return

    setSaving(true)
    setError('')
    try {
      await contractService.deleteGarantia(Number(item.id))
      setMessage('Garantia eliminada')
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo eliminar la garantia')
    } finally {
      setSaving(false)
    }
  }

  if (!isAdmin && !loading) {
    return (
      <MainLayout>
        <div className="p-6">
          <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg p-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Garantías</h1>
            <p className="text-gray-600">Solo los administradores pueden administrar esta sección.</p>
            <div className="mt-4">
              <Button onClick={() => navigate('/settings')} variant="outline">
                Volver a configuración
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Garantías</h1>
            <p className="text-gray-600 mt-1">Catálogo global de garantías para contratos</p>
          </div>
          <Button onClick={openCreate} disabled={loading}>
            Nueva Garantia
          </Button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 whitespace-pre-line">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 whitespace-pre-line">
            {message}
          </div>
        )}

        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Nombre</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Descripcion</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Tipo</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Cargando garantias...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No hay garantias cargadas.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{item.nombre}</td>
                    <td className="px-4 py-3 text-gray-700">{item.descripcion || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                        item.es_predefinida ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {item.es_predefinida ? 'Predefinida' : 'Personalizada'}
                      </span>
                      {!!item.contratos_count && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                          {item.contratos_count} uso(s)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          disabled={item.es_predefinida}
                          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={item.es_predefinida || saving}
                          className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editing ? 'Editar garantia' : 'Nueva garantia'}
          size="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                value={form.nombre}
                onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nombre de la garantia"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
              <textarea
                value={form.descripcion}
                onChange={(e) => setForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descripcion opcional"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  )
}
